/**
 * Aspect Admin — Files / assets / media / upload management.
 */

import { icon } from "./icons.js";
import { renderShell, pageHead, toast, meter, openDrawer } from "./shell.js";
import { escapeHtml } from "./auth_shell.js";
import { getSession } from "../auth_store.js";
import { navigate } from "../router.js";
import { t } from "../i18n.js";
import { FILES } from "../mock_data.js";

let state = { type: "all", query: "" };

const TYPES = [
  ["all", "All files"],
  ["Floor plan", "Floor plans"],
  ["Photo", "Photos"],
  ["3D export", "3D exports"],
  ["Document", "Documents"],
];

function fileRow(f) {
  return `
    <tr data-row data-file="${f.id}">
      <td>
        <div class="ap-entity">
          <div class="ap-avatar">${icon(f.kind, 15)}</div>
          <div>
            <span class="ap-cell-main">${escapeHtml(f.name)}</span>
            <span class="ap-cell-sub">${t(f.type)} · ${f.size}</span>
          </div>
        </div>
      </td>
      <td>${f.space === "—" ? `<span class="ap-note">${t("Workspace")}</span>` : escapeHtml(f.space)}</td>
      <td>${escapeHtml(t(f.by))}</td>
      <td>${t(f.uploaded)}</td>
      <td class="ap-cell-actions">
        <button type="button" class="ap-iconbtn ap-iconbtn--bare" data-act="download" title="${t("Download")}">${icon("download", 15)}</button>
        <button type="button" class="ap-iconbtn ap-iconbtn--bare" data-act="delete" title="${t("Delete")}">${icon("trash", 15)}</button>
      </td>
    </tr>
  `;
}

function fileDrawer(root, f) {
  openDrawer(root, {
    title: f.name,
    subtitle: `${t(f.type)} · ${f.size} · ${t("uploaded")} ${t(f.uploaded)}`,
    bodyHtml: `
      <div class="ap-space__thumb" style="border-radius:var(--ap-r);border:1px solid var(--ap-hairline);aspect-ratio:16/10;">${icon(f.kind, 40)}</div>
      <div class="ap-drawer__section">
        <h4>${t("Details")}</h4>
        <dl class="ap-def">
          <dt>${t("Type")}</dt><dd>${t(f.type)}</dd>
          <dt>${t("Size")}</dt><dd>${f.size}</dd>
          <dt>${t("Linked space")}</dt><dd>${escapeHtml(f.space)}</dd>
          <dt>${t("Uploaded by")}</dt><dd>${escapeHtml(t(f.by))}</dd>
          <dt>${t("Uploaded")}</dt><dd>${t(f.uploaded)}</dd>
        </dl>
      </div>
      ${f.type === "Floor plan" ? `
      <div class="ap-insight">
        <div class="ap-insight__head">${icon("sparkles", 15)}<span class="ap-insight__tag">${t("AI ready")}</span></div>
        <p class="ap-insight__body">${t("This plan can be analyzed to generate a 3D space automatically. Estimated processing time: ~90 seconds.")}</p>
        <div class="ap-insight__foot"><span></span><button type="button" class="ap-btn ap-btn--soft ap-btn--sm" data-drawer-analyze>${icon("wand", 13)} ${t("Analyze plan")}</button></div>
      </div>` : ""}
    `,
    footHtml: `
      <button type="button" class="ap-btn" data-drawer-dl>${icon("download", 15)} ${t("Download")}</button>
      <button type="button" class="ap-btn ap-btn--danger" data-drawer-del>${icon("trash", 15)} ${t("Delete")}</button>
    `,
  });
  const slot = root.querySelector("#ap-overlay-slot");
  slot?.querySelector("[data-drawer-analyze]")?.addEventListener("click", () => toast(root, t("Plan analysis queued — track it in AI Jobs"), "ok"));
  slot?.querySelector("[data-drawer-dl]")?.addEventListener("click", () => toast(root, t("{f} downloading…", { f: f.name }), "ok"));
  slot?.querySelector("[data-drawer-del]")?.addEventListener("click", () => toast(root, t("{f} moved to trash", { f: f.name }), "info"));
}

export function renderFiles(root) {
  const session = getSession();
  if (!session) { navigate("/login", true); return; }

  const q = state.query.trim().toLowerCase();
  const list = FILES.filter((f) => (state.type === "all" || f.type === state.type) && (!q || f.name.toLowerCase().includes(q)));

  const content = `
    ${pageHead(t("Files"), t("Floor plans, photos, 3D exports, and brand assets across the workspace."), `
      <button type="button" class="ap-btn ap-btn--primary" id="fl-upload">${icon("upload", 15)} ${t("Upload files")}</button>
    `)}

    <div class="ap-grid ap-grid--main" style="grid-template-columns:minmax(0,2.4fr) minmax(280px,1fr);">
      <div class="ap-stack">
        <div class="ap-dropzone" id="fl-drop" role="button" tabindex="0">
          ${icon("upload", 26)}
          <span><strong>${t("Drop files here")}</strong> ${t("or click to browse")}</span>
          <span class="ap-note">${t("Floor plans (PNG, JPG, PDF, DWG) · photos · 3D models up to 250 MB")}</span>
        </div>

        <div class="ap-toolbar" style="margin-bottom:0;">
          <div class="ap-chiprow">
            ${TYPES.map(([id, label]) => `<button type="button" class="ap-chip ${state.type === id ? "ap-chip--on" : ""}" data-type="${id}">${t(label)}</button>`).join("")}
          </div>
          <div class="ap-toolbar__spacer"></div>
          <div class="ap-searchfield" style="width:210px;">
            ${icon("search", 15)}
            <input class="ap-input" id="fl-search" type="text" placeholder="${t("Search files…")}" value="${escapeHtml(state.query)}" />
          </div>
        </div>

        <div class="ap-panel">
          <div class="ap-tablewrap ap-scroll">
            <table class="ap-table">
              <thead><tr><th>${t("File")}</th><th>${t("Space")}</th><th>${t("Uploaded by")}</th><th>${t("Date")}</th><th></th></tr></thead>
              <tbody>${list.map(fileRow).join("")}</tbody>
            </table>
          </div>
          <div class="ap-panel__foot">
            <span>${t("{n} files", { n: list.length })}</span>
            <span>${t("Deleted files are recoverable for 30 days")}</span>
          </div>
        </div>
      </div>

      <div class="ap-stack">
        <div class="ap-panel ap-panel--pad">
          <div class="ap-panel__head"><h3 class="ap-panel__title">${t("Storage")}</h3></div>
          ${meter("Asset storage", 412, 1024, "GB")}
          <div class="ap-stack" style="gap:0.4rem;margin-top:0.9rem;">
            ${[
              ["3D exports", "218 GB", "cube"],
              ["Photos", "121 GB", "image"],
              ["Floor plans", "48 GB", "doc"],
              ["Other", "25 GB", "folder"],
            ]
              .map(
                ([label, size, ic]) => `
              <div class="ap-healthrow">
                <span class="ap-healthrow__name">${icon(ic, 14)} ${t(label)}</span>
                <span class="ap-note" style="font-variant-numeric:tabular-nums;">${size}</span>
              </div>`
              )
              .join("")}
          </div>
        </div>

        <div class="ap-insight">
          <div class="ap-insight__head">${icon("sparkles", 15)}<span class="ap-insight__tag">${t("Housekeeping")}</span></div>
          <h4 class="ap-insight__title">${t("64 GB of unused exports found")}</h4>
          <p class="ap-insight__body">${t("11 tour exports haven't been accessed in 90+ days and their spaces have newer versions. Archiving them frees 6% of your storage.")}</p>
          <div class="ap-insight__foot">
            <span class="ap-conf">${t("Confidence")} <span class="ap-conf__bars"><i class="on"></i><i class="on"></i><i class="on"></i></span></span>
            <button type="button" class="ap-btn ap-btn--soft ap-btn--sm" id="fl-archive">${t("Archive 11 files")}</button>
          </div>
        </div>
      </div>
    </div>
  `;

  const el = renderShell(root, "/files", content);
  if (!el) return;

  const drop = el.querySelector("#fl-drop");
  const pulse = () => toast(root, t("Upload started — files appear here when processed"), "ok");
  drop?.addEventListener("click", pulse);
  drop?.addEventListener("dragover", (e) => { e.preventDefault(); drop.classList.add("ap-dropzone--over"); });
  drop?.addEventListener("dragleave", () => drop.classList.remove("ap-dropzone--over"));
  drop?.addEventListener("drop", (e) => { e.preventDefault(); drop.classList.remove("ap-dropzone--over"); pulse(); });

  el.querySelector("#fl-upload")?.addEventListener("click", pulse);
  el.querySelector("#fl-archive")?.addEventListener("click", () => toast(root, t("11 files archived — 64 GB freed"), "ok"));

  el.querySelectorAll("[data-type]").forEach((c) =>
    c.addEventListener("click", () => {
      state = { ...state, type: c.dataset.type };
      renderFiles(root);
    })
  );

  const search = el.querySelector("#fl-search");
  search?.addEventListener("input", () => {
    state = { ...state, query: search.value };
    clearTimeout(search._t);
    search._t = setTimeout(() => {
      const pos = search.selectionStart;
      renderFiles(root);
      const s2 = root.querySelector("#fl-search");
      s2?.focus();
      s2?.setSelectionRange(pos, pos);
    }, 240);
  });

  el.querySelector("tbody")?.addEventListener("click", (e) => {
    const row = e.target.closest("[data-file]");
    if (!row) return;
    const f = FILES.find((x) => x.id === row.dataset.file);
    if (!f) return;
    const act = e.target.closest("[data-act]")?.dataset?.act;
    if (act === "download") { toast(root, t("{f} downloading…", { f: f.name }), "ok"); return; }
    if (act === "delete") { toast(root, t("{f} moved to trash", { f: f.name }), "info"); return; }
    fileDrawer(root, f);
  });
}
